import { t } from "i18next";
import type { OfflineChunk, OfflineRecording } from "./types";

/**
 * IndexedDB storage for offline-safe recordings.
 *
 * - `recordings` (key `id`): one row per recording, see {@link OfflineRecording}.
 * - `chunks` (key `[recordingId, seq]`): the MediaRecorder output in order,
 *   as ArrayBuffers (structured-clone safe in every browser).
 *
 * Each chunk is written in the same transaction as the row's byte count and
 * duration, so the row always describes the audio that is really stored.
 */
export const OFFLINE_RECORDING_DB = "rosc-offline-recordings";
const VERSION = 1;
const RECORDINGS = "recordings";
const CHUNKS = "chunks";

export class StorageFullError extends Error {
  constructor(message = t("offlineRecording.errors.storageFull")) {
    super(message);
    this.name = "StorageFullError";
  }
}

export class OfflineStorageUnavailableError extends Error {
  constructor(message = t("offlineRecording.errors.storageUnavailable")) {
    super(message);
    this.name = "OfflineStorageUnavailableError";
  }
}

/** The row was discarded while a chunk was being written. */
export class RecordingRemovedError extends Error {
  constructor() {
    super(t("offlineRecording.errors.removed"));
    this.name = "RecordingRemovedError";
  }
}

const isQuotaError = (error: unknown): boolean =>
  error instanceof DOMException
    ? error.name === "QuotaExceededError" || error.code === 22
    : error instanceof Error && error.name === "QuotaExceededError";

const mapError = (error: unknown): Error => {
  if (isQuotaError(error)) return new StorageFullError();
  return error instanceof Error
    ? error
    : new Error("Recording storage failed on this device.");
};

let opening: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
  if (opening) return opening;
  opening = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new OfflineStorageUnavailableError());
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(OFFLINE_RECORDING_DB, VERSION);
    } catch (error) {
      reject(mapError(error));
      return;
    }
    request.onupgradeneeded = (): void => {
      const db = request.result;
      if (!db.objectStoreNames.contains(RECORDINGS))
        db.createObjectStore(RECORDINGS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(CHUNKS))
        db.createObjectStore(CHUNKS, { keyPath: ["recordingId", "seq"] });
    };
    request.onsuccess = (): void => {
      const db = request.result;
      // Another tab upgrading the schema: let it, reopen on next use.
      db.onversionchange = (): void => {
        db.close();
        opening = null;
      };
      resolve(db);
    };
    request.onerror = (): void =>
      reject(
        request.error
          ? mapError(request.error)
          : new OfflineStorageUnavailableError(),
      );
    request.onblocked = (): void => undefined;
  });
  opening.catch(() => {
    opening = null;
  });
  return opening;
};

/** Closes the cached connection (tests simulate a reload with this). */
export const closeOfflineRecordingDb = async (): Promise<void> => {
  const current = opening;
  opening = null;
  if (!current) return;
  try {
    (await current).close();
  } catch {}
};

const run = async <T>(
  stores: string[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => T | Promise<T>,
): Promise<T> => {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(stores, mode);
    } catch (error) {
      reject(mapError(error));
      return;
    }
    let result: T;
    let failed: unknown = null;
    tx.oncomplete = (): void => resolve(result);
    tx.onerror = (): void => reject(mapError(tx.error ?? failed));
    tx.onabort = (): void => reject(mapError(tx.error ?? failed));
    Promise.resolve()
      .then(() => work(tx))
      .then(
        (value) => {
          result = value;
        },
        (error: unknown) => {
          failed = error;
          try {
            tx.abort();
          } catch {}
        },
      );
  });
};

const request = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void => reject(mapError(req.error));
  });

const chunkRange = (id: string): IDBKeyRange =>
  IDBKeyRange.bound([id, 0], [id, Number.MAX_SAFE_INTEGER]);

export const putRecording = (recording: OfflineRecording): Promise<void> =>
  run([RECORDINGS], "readwrite", (tx) => {
    tx.objectStore(RECORDINGS).put(recording);
  });

export const getRecording = (
  id: string,
): Promise<OfflineRecording | undefined> =>
  run([RECORDINGS], "readonly", (tx) =>
    request<OfflineRecording | undefined>(tx.objectStore(RECORDINGS).get(id)),
  );

export const listRecordings = (): Promise<OfflineRecording[]> =>
  run([RECORDINGS], "readonly", (tx) =>
    request<OfflineRecording[]>(tx.objectStore(RECORDINGS).getAll()),
  );

/**
 * Reads and rewrites a row in one transaction. `change` returns the new row,
 * or `null` to leave it unchanged. Resolves to the stored row.
 */
export const updateRecording = (
  id: string,
  change: (current: OfflineRecording) => OfflineRecording | null,
): Promise<OfflineRecording | undefined> =>
  run([RECORDINGS], "readwrite", async (tx) => {
    const store = tx.objectStore(RECORDINGS);
    const current = await request<OfflineRecording | undefined>(store.get(id));
    if (!current) return undefined;
    const next = change(current);
    if (!next) return current;
    store.put(next);
    return next;
  });

/** Stores one chunk and updates the row's totals atomically. */
export const appendChunk = (
  id: string,
  seq: number,
  data: ArrayBuffer,
  totals: { durationMs: number; now: number },
): Promise<void> =>
  run([RECORDINGS, CHUNKS], "readwrite", async (tx) => {
    const store = tx.objectStore(RECORDINGS);
    const current = await request<OfflineRecording | undefined>(store.get(id));
    if (!current) throw new RecordingRemovedError();
    const chunk: OfflineChunk = { recordingId: id, seq, data };
    tx.objectStore(CHUNKS).put(chunk);
    store.put({
      ...current,
      bytes: current.bytes + data.byteLength,
      chunkCount: Math.max(current.chunkCount, seq + 1),
      durationMs: Math.max(current.durationMs, totals.durationMs),
      updatedAt: totals.now,
      heartbeatAt: totals.now,
    });
  });

/** The stored audio as one Blob, chunks in recording order. */
export const readRecordingAudio = (
  id: string,
  mimeType: string,
): Promise<Blob> =>
  run([CHUNKS], "readonly", async (tx) => {
    const chunks = await request<OfflineChunk[]>(
      tx.objectStore(CHUNKS).getAll(chunkRange(id)),
    );
    chunks.sort((a, b) => a.seq - b.seq);
    return new Blob(
      chunks.map((chunk) => chunk.data),
      { type: mimeType },
    );
  });

/** Removes the row and all of its audio. */
export const deleteRecording = (id: string): Promise<void> =>
  run([RECORDINGS, CHUNKS], "readwrite", (tx) => {
    tx.objectStore(RECORDINGS).delete(id);
    tx.objectStore(CHUNKS).delete(chunkRange(id));
  });
